// Single Supabase browser client, bound to the user session. Only the anon key is ever
// used on the front: the service role key stays server side (Edge Functions and CI).
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { erreurLocale } from './erreurs.js';

let instance: SupabaseClient | null = null;

interface EnvVite {
  readonly VITE_SUPABASE_URL?: string;
  readonly VITE_SUPABASE_ANON_KEY?: string;
}

function env(): EnvVite {
  // import.meta.env is injected by Vite; typed locally to avoid a vite/client dependency.
  return (import.meta as unknown as { env?: EnvVite }).env ?? {};
}

/** Lazily built, session persisted Supabase client. Throws if the env is not configured. */
export function getClient(): SupabaseClient {
  if (instance !== null) {
    return instance;
  }
  const { VITE_SUPABASE_URL: url, VITE_SUPABASE_ANON_KEY: anon } = env();
  if (url === undefined || anon === undefined || url === '' || anon === '') {
    throw erreurLocale(
      'ERREUR_INTERNE',
      'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set to reach the backend.',
    );
  }
  instance = createClient(url, anon, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    global: { headers: { 'x-application': 'horizon-web' } },
  });
  return instance;
}

/** Reset the singleton; used by tests and after a sign out. */
export function reinitialiserClient(): void {
  instance = null;
}

export type { SupabaseClient };

/**
 * Ready to use client. It is a lazy proxy: the underlying singleton is created on the
 * first property access, so importing this module never requires the environment to be
 * configured yet. Behaves like a SupabaseClient in every other respect.
 */
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_cible, propriete) {
    const reel = getClient() as unknown as Record<string | symbol, unknown>;
    const valeur = reel[propriete];
    return typeof valeur === 'function' ? valeur.bind(reel) : valeur;
  },
});
